import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'

import { generatePredeterminedColor } from '../../../../../utilities/helpers'

import { ActionsProvider } from '../../../../../providers/ActionsProvider/ActionsProvider'
import { useInventory } from '../../InventoryProvider/InventoryProvider'
import { Sidebar } from './Sidebar'
import { SeatDetailsPanel } from './Sidebar/SeatDetailsPanel'
import { SeatMap } from '../../../../SeatMap'

import './mapConfigurations.scss'
import { TooltipProvider } from '../../../../../providers/TooltipProvider/TooltipProvider'
import {
  getVenueSeatScale,
  isNmsuStyleSeatmapVenue,
} from '../../../../../utilities/frontendSeatmapSizing'
import {
  buildPurchaserClusters,
  isPurchasedSeatIdentity,
  PURCHASED_SEAT_STATUSES,
  getPurchaserClusterSeatIds,
} from './purchaserClusterUtils'
import { getPurchaserClusterKey } from '../../../../../utilities/purchaserSeatClusters'
import { getAssignedGaSpots } from '../gaSeatInventory'
import {
  getGaSectionSourceId,
  preferUnassignedSeatIds,
} from '../scalingSeatSelection'
import { buildSeatLabelLookup } from '../../../../../utilities/seatLabels'

export default function MapConfigurations({
  isEventOnsale,
  allowResales,
  data,
  background,
  activeTab,
  isMapDisabled,
  priceLevels,
  setPriceLevels,
  unassignedSeats,
  getDefaultObj,
  getObjectTotalSeatCount,
  venueSlug,
  venueName,
  seatIdentityById = {},
  eventUuid,
  eventTimezone,
  eventSeatLabels = {},
}) {
  const SELECTED_COLOR = 'rgba(20, 20, 22, 1)'
  const SOLD_COLOR = '#45B26B'
  const {
    HOLDSCOLOR,
    KILLSCOLOR,
    offers,
    kills,
    holds,
    setHolds,
    setKills,
    sold,
    resale,
    selectedSeatIds,
    setSelectedSeatIds,
    getSeatItem,
    getGAPLIDFrom,
    isSectionSelected,
    getGASectionColor,
    clearSelectedSeats,
    scalingLookupTable,
  } = useInventory()

  const [selectedGASectionId, setSelectedGASectionId] = useState(null)
  const [showSelectGASeats, setShowSelectGASeats] = useState(false)
  const [hoveredClusterKey, setHoveredClusterKey] = useState(null)
  const [purchaserFocus, setPurchaserFocus] = useState(null)
  const [seatLabels, setSeatLabels] = useState(() =>
    buildSeatLabelLookup(data, eventSeatLabels)
  )

  useEffect(() => {
    setSeatLabels(buildSeatLabelLookup(data, eventSeatLabels))
  }, [data, eventSeatLabels])

  const { purchaserKeyToSeatIds: clusterKeyToSeatIds } = useMemo(
    () => buildPurchaserClusters(seatIdentityById),
    [seatIdentityById]
  )

  // modify sidebar container when seats are selected or purchaser panel is open
  useLayoutEffect(() => {
    const el = document.querySelector('.cef-seatmap-wrapper')
    if (!el) return undefined

    const hasDragSelection =
      (activeTab === 'scaling' || activeTab === 'inventory') &&
      Object.keys(selectedSeatIds).length > 0
    const hasPurchaserPanel =
      activeTab === 'inventory' && purchaserFocus != null

    el.classList.toggle('panel-open', hasDragSelection)
    el.classList.toggle('purchaser-panel-open', hasPurchaserPanel)

    return () => {
      el.classList.remove('panel-open')
      el.classList.remove('purchaser-panel-open')
    }
  }, [selectedSeatIds, activeTab, purchaserFocus])

  useLayoutEffect(() => {
    if (activeTab !== 'inventory') {
      setPurchaserFocus(null)
      setHoveredClusterKey(null)
    }
  }, [activeTab])

  const closePurchaserPanel = useCallback(() => {
    setPurchaserFocus(null)
    setHoveredClusterKey(null)
  }, [])

  const selectPurchaserCluster = useCallback(
    (seatId) => {
      const identity = seatIdentityById[seatId]
      if (!isPurchasedSeatIdentity(identity)) return

      if (purchaserFocus?.anchorSeatId === seatId) {
        closePurchaserPanel()
        return
      }

      const clusterKey = getPurchaserClusterKey(identity)
      const seatIds = getPurchaserClusterSeatIds(seatId, seatIdentityById, {
        purchaserKeyToSeatIds: clusterKeyToSeatIds,
      })
      clearSelectedSeats()
      setPurchaserFocus({
        clusterKey,
        anchorSeatId: seatId,
        seatIds,
      })
      setHoveredClusterKey(null)
    },
    [
      seatIdentityById,
      clusterKeyToSeatIds,
      clearSelectedSeats,
      purchaserFocus,
      closePurchaserPanel,
    ]
  )

  // add new pricing level, hold, or kill object
  const handleAdd = (type, id) => {
    const generateKey = (obj) => {
      const keys = Object.keys(obj)
      if (keys.length === 0) {
        return keys.length + 1
      } else {
        return Math.max(...keys) + 1
      }
    }

    const createHoldKill = (prevState, id, color, name) => {
      const index = generateKey(prevState)

      if (id) {
        const catIndex = generateKey(prevState[id]?.categories)
        return {
          ...prevState,
          [id]: {
            ...prevState[id],
            categories: {
              ...prevState[id].categories,
              [catIndex]: {
                ...getDefaultObj(catIndex, `Category ${catIndex}`, color),
              },
            },
          },
        }
      } else {
        return {
          ...prevState,
          [index]: {
            ...getDefaultObj(index, `${index} ${name}`, color),
            categories: {},
          },
        }
      }
    }

    switch (type) {
      case 'level':
        const index = generateKey(priceLevels)
        setPriceLevels((prevState) => ({
          ...prevState,
          [index]: {
            ...getDefaultObj(
              index,
              `P${index}`,
              generatePredeterminedColor(index)
            ),
            price: 0,
          },
        }))
        break

      case 'hold':
        setHolds((prevState) =>
          createHoldKill(prevState, id, HOLDSCOLOR, 'Hold')
        )
        break

      case 'kill':
        setKills((prevState) =>
          createHoldKill(prevState, id, KILLSCOLOR, 'Kill')
        )
        break

      default:
        break
    }
  }

  const preferUnassignedSeats = useCallback(
    (seatIds) =>
      preferUnassignedSeatIds(seatIds, (seatId) =>
        Boolean(scalingLookupTable[seatId])
      ),
    [scalingLookupTable]
  )

  const selectUnassigned = () => {
    closePurchaserPanel()
    const unassignedObject = Object.values(unassignedSeats)[0]
    const unassignedIds = []

    unassignedIds.push(...unassignedObject.seats)

    selectRegularSeats(null, unassignedIds)

    if (unassignedObject.gaSeats) {
      const addToGASeats = {}

      for (const [sectionId, seats] of Object.entries(
        unassignedObject.gaSeats
      )) {
        addToGASeats[sectionId] = {
          unassigned: seats,
        }
      }

      selectGASeats(addToGASeats)
    }
  }

  const clickGASection = (section) => {
    if (activeTab === 'inventory') {
      clickOpenGAModal(section)
      return
    }

    const sectionPLRes = getGAPLIDFrom(section.sectionId)
    const unassignedGaSeats = unassignedSeats?.unassigned?.gaSeats
    const fromIdKey = getGaSectionSourceId(
      section.sectionId,
      sectionPLRes,
      unassignedGaSeats
    )
    const sourceGaSeats =
      fromIdKey === 'unassigned'
        ? unassignedGaSeats
        : priceLevels?.[sectionPLRes]?.gaSeats
    const addToGASeats = {
      [section.sectionId]: {
        [fromIdKey]: getAssignedGaSpots(
          sourceGaSeats,
          section.sectionId,
          section.spots
        ),
      },
    }

    selectGASeats(addToGASeats, { replaceSection: true })
  }

  const selectGASeats = (gaSeatsToSelect, { replaceSection = false } = {}) => {
    closePurchaserPanel()
    setSelectedSeatIds((prevSelected) => {
      const newSelected = { ...prevSelected }
      if (!newSelected.gaSeats) {
        newSelected.gaSeats = {}
      }

      for (const [sectionId, fromSeats] of Object.entries(gaSeatsToSelect)) {
        if (replaceSection || !newSelected.gaSeats[sectionId]) {
          newSelected.gaSeats[sectionId] = {}
        }

        for (const [fromId, seats] of Object.entries(fromSeats)) {
          if (!newSelected.gaSeats[sectionId][fromId]) {
            newSelected.gaSeats[sectionId][fromId] = []
          }

          const existingSeats = new Set(newSelected.gaSeats[sectionId][fromId])
          const uniqueNewSeats = seats.filter(
            (seat) => !existingSeats.has(seat)
          )

          newSelected.gaSeats[sectionId][fromId].push(...uniqueNewSeats)
        }
      }

      return newSelected
    })
  }

  const selectRegularSeats = (seatId, seatIdArray) => {
    if (isMapDisabled) return

    closePurchaserPanel()

    setSelectedSeatIds((prevSelected) => {
      const newSelected = { ...prevSelected }
      if (!newSelected.seats) {
        newSelected.seats = {}
      }

      const addSeat = (id) => {
        const seatItem = getSeatItem(id)
        const key = seatItem && seatItem.key ? seatItem.key : 'unassigned'

        if (!seatItem.disabled) {
          if (!newSelected.seats[key]) {
            newSelected.seats[key] = []
          }
          if (!newSelected.seats[key].includes(id)) {
            newSelected.seats[key].push(id)
          }
        }
      }

      if (seatId) {
        addSeat(seatId)
      } else if (seatIdArray) {
        seatIdArray.forEach(addSeat)
      }

      return newSelected
    })
  }

  const handleSeatSelect = (seatId, seatIdArray) => {
    const ids = seatId
      ? [seatId]
      : Array.isArray(seatIdArray)
        ? seatIdArray
        : []

    if (!ids.length) return

    if (activeTab === 'inventory') {
      const purchasedIds = ids.filter((id) =>
        isPurchasedSeatIdentity(seatIdentityById[id])
      )
      const selectableIds = ids.filter(
        (id) => !isPurchasedSeatIdentity(seatIdentityById[id])
      )

      if (ids.length === 1 && purchasedIds.length === 1) {
        selectPurchaserCluster(purchasedIds[0])
        return
      }

      if (selectableIds.length === 1) {
        selectRegularSeats(selectableIds[0])
      } else if (selectableIds.length > 1) {
        selectRegularSeats(undefined, selectableIds)
      }
      return
    }

    if (seatId) {
      selectRegularSeats(seatId)
    } else if (seatIdArray?.length) {
      selectRegularSeats(undefined, preferUnassignedSeats(seatIdArray))
    }
  }

  const clickOpenGAModal = (section) => {
    setSelectedGASectionId(section?.sectionId)
    setShowSelectGASeats(true)
  }

  const getSeatIdsForZoomableSection = (section, rows, seats) => {
    return Object.values(rows)
      .filter((row) => row.sectionId === section.sectionId)
      .flatMap((row) => row.seats)
      .filter((seatId) => {
        if (!seats[seatId]) return false
        return true
      })
  }

  const getSectionState = (section) => {
    if (isSectionSelected(section.sectionId)) {
      return { disabled: false, color: SELECTED_COLOR }
    }

    const gaColor = getGASectionColor(section.sectionId)
    if (gaColor && activeTab === 'scaling') {
      return { disabled: false, color: gaColor }
    }

    return { disabled: false, color: section.fill }
  }

  const getSeatState = (seatId) => {
    const base = getSeatItem(seatId)
    const identity = seatIdentityById[seatId]
    const focusSeatIds = purchaserFocus?.seatIds || []
    const isInFocus = activeTab === 'inventory' && focusSeatIds.includes(seatId)

    if (isInFocus) {
      const soldColor = base?.color || SOLD_COLOR
      return {
        ...base,
        color: soldColor,
        disabled: false,
        clusterHighlight: 'selected',
        isClusterAnchor: seatId === purchaserFocus?.anchorSeatId,
        additionalLabel: seatLabels[seatId] || null,
      }
    }

    if (activeTab !== 'inventory' || !isPurchasedSeatIdentity(identity)) {
      return {
        ...base,
        additionalLabel: seatLabels[seatId] || null,
      }
    }

    const soldColor = base?.color || SOLD_COLOR
    const isInHoverCluster =
      hoveredClusterKey &&
      (clusterKeyToSeatIds[hoveredClusterKey] || []).includes(seatId)

    if (isInHoverCluster) {
      return {
        ...base,
        color: soldColor,
        disabled: false,
        clusterHighlight: 'hover',
        additionalLabel: seatLabels[seatId] || null,
      }
    }

    return {
      ...base,
      color: soldColor,
      disabled: false,
      additionalLabel: seatLabels[seatId] || null,
    }
  }

  const getSeatTooltipDetails = (seatId) => {
    const identity = seatIdentityById?.[seatId]
    const additionalLabel = seatLabels?.[seatId] || null
    const isPurchased =
      identity && PURCHASED_SEAT_STATUSES.includes(identity.onSaleStatus)

    if (!isPurchased && !additionalLabel) return null

    return {
      ...(identity || {}),
      additionalLabel,
      clusterKey: isPurchased ? getPurchaserClusterKey(identity) : null,
    }
  }

  const useFrontendSeatSizing = isNmsuStyleSeatmapVenue(venueSlug)
  const seatScale = getVenueSeatScale({ venueName, venueSlug })
  const seatBorderRadiusForSizing =
    typeof data?.seat_border_radius === 'number'
      ? data.seat_border_radius
      : undefined

  const handleSectionSelect = (section) => {
    if (isMapDisabled) return

    if (section?.zoomable) {
      const seatIds = getSeatIdsForZoomableSection(
        section,
        data.rows,
        data.seats
      )
      if (activeTab === 'inventory') {
        const purchasedIds = seatIds.filter((id) =>
          isPurchasedSeatIdentity(seatIdentityById[id])
        )
        const selectableIds = seatIds.filter(
          (id) => !isPurchasedSeatIdentity(seatIdentityById[id])
        )

        if (purchasedIds.length === 1 && selectableIds.length === 0) {
          selectPurchaserCluster(purchasedIds[0])
          return
        }

        if (selectableIds.length) {
          selectRegularSeats(undefined, selectableIds)
        }
        return
      }
      selectRegularSeats(undefined, preferUnassignedSeats(seatIds))
    } else {
      clickGASection(data.sections[section.sectionId])
    }
  }

  return (
    <>
      <Sidebar
        tab={activeTab}
        handleAdd={handleAdd}
        isEventOnsale={isEventOnsale}
        allowResales={allowResales}
        isMapDisabled={isMapDisabled}
        showSelectGASeats={showSelectGASeats}
        setShowSelectGASeats={setShowSelectGASeats}
        selectedGASectionId={selectedGASectionId}
        selectUnassigned={selectUnassigned}
        selectGASeats={selectGASeats}
        getObjectTotalSeatCount={getObjectTotalSeatCount}
        purchaserFocusActive={purchaserFocus != null}
        eventUuid={eventUuid}
        seatLabels={seatLabels}
        onLabelsChange={setSeatLabels}
      />
      {/* css wrapper for positioning, improves re-usability of actionsProvider + seatMap */}
      <div className="cef-seatmap-wrapper" data-seatmap-tour="map">
        <TooltipProvider>
          <ActionsProvider canEdit={!isMapDisabled}>
            <SeatMap
              data={data}
              background={background}
              isMapDisabled={isMapDisabled}
              useFrontendSeatSizing={useFrontendSeatSizing}
              seatScale={seatScale}
              seatBorderRadius={seatBorderRadiusForSizing}
              selectRegularSeats={handleSeatSelect}
              handleSectionSelect={handleSectionSelect}
              getSectionState={getSectionState}
              getSeatState={getSeatState}
              getSeatTooltipDetails={getSeatTooltipDetails}
              onPurchaserClusterHover={setHoveredClusterKey}
              displayInventoryInfo={activeTab === 'inventory'}
              deriveInventoryInfoFrom={[
                { inventory: offers, type: 'offers' },
                { inventory: kills, type: 'kills' },
                { inventory: holds, type: 'holds' },
                { inventory: sold, type: 'sold' },
                { inventory: resale, type: 'resale' },
              ]}
            />
          </ActionsProvider>
        </TooltipProvider>
        {activeTab === 'inventory' && purchaserFocus && (
          <SeatDetailsPanel
            eventUuid={eventUuid}
            anchorSeatId={purchaserFocus.anchorSeatId}
            seatIdentity={seatIdentityById[purchaserFocus.anchorSeatId]}
            timezone={eventTimezone}
            onClose={closePurchaserPanel}
            seatLabels={seatLabels}
            onLabelsChange={setSeatLabels}
            canEditLabels={!isMapDisabled}
          />
        )}
      </div>
    </>
  )
}
